import { includes, zipObject, cloneDeep, isEmpty } from 'lodash';

import PET_CONFIG from 'config/pet.config';
import {
  apiGetRequest,
  generateApiRequestHeaders,
  apiPostRequest,
} from 'utils/http/apiHttp';
import { date } from 'utils/date';
import {
  isListingStatusLive,
  isListingStatusCreated,
  isListingPetWorkflowStatusForwarded,
  isListingPetWorkflowStatusInProgress,
  isListingPetWorkflowStatusPendingReview,
} from 'utils/listing/checkers';
import { mediaVisibilityId } from 'utils/media/mediaTypes';
import {
  isMediaPhoto,
  isMediaVideo,
  getOrderedImages,
  getCoverImages,
  getAdditionalImages,
  getHiddenImages,
  getRestOfImages,
  getOtherMedia,
  getSortedImagesByViewOrder,
  isMediaPendingReview,
  getDeletedMedia,
  isMediaHidden,
} from 'utils/media/media';
import { userSession } from 'utils/user/userSession';
import { environmentVariables } from 'utils/environmentVariables';

const apiUrl = environmentVariables.REACT_APP_API_HOST;

// *** LISTING GET
// **********************************************************
export const getListing = async (listingId) => {
  const response = await apiGetRequest(
    `listing/${listingId}`,
    `withMedia=1&withNotes=1&withContractTerms=1`
  );
  const listing = response?.json?.listing;
  return listing;
};

export const getNextListingIdByType = async (type) => {
  const response = await apiGetRequest('listing/request', `only=${type}`);
  const listingsAvailable = response?.json?.listingsAvailable;
  const listingId = listingsAvailable?.litter?.listings?.[0]?.id;
  return listingId || type;
};

export const getListingNotes = async (listingId) => {
  const headers = generateApiRequestHeaders();
  const request = await fetch(
    `${apiUrl}/api/listing/${listingId}/note?application=${environmentVariables.REACT_APP_APPLICATION_ID}`,
    {
      method: 'GET',
      headers,
    }
  );
  const data = await request.clone().json();
  return data?.notes;
};

export const getBreederNotes = async (breederId) => {
  const headers = generateApiRequestHeaders();
  const request = await fetch(
    `${apiUrl}/api/breeder/${breederId}/note?application=${environmentVariables.REACT_APP_APPLICATION_ID}`,
    {
      method: 'GET',
      headers,
    }
  );
  const data = await request.clone().json();
  return data.notes;
};

export const getPuppyNotes = async (puppyId) => {
  const headers = generateApiRequestHeaders();
  const request = await fetch(
    `${apiUrl}/api/puppy/${puppyId}/note?application=${environmentVariables.REACT_APP_APPLICATION_ID}`,
    {
      method: 'GET',
      headers,
    }
  );
  const data = await request.clone().json();
  return data.notes;
};

export const getBreeder = async (breederId) => {
  const headers = generateApiRequestHeaders();
  const requestBreeder = await fetch(
    `${apiUrl}/api/breeder/${breederId}?application=${environmentVariables.REACT_APP_APPLICATION_ID}`,
    {
      method: 'GET',
      headers,
    }
  );
  const dataBreeder = await requestBreeder.clone().json();

  const requestAccountRepUser = await fetch(
    `${apiUrl}/api/access/user/${dataBreeder.breeder.accountRepUserId}?application=${environmentVariables.REACT_APP_APPLICATION_ID}`,
    {
      method: 'GET',
      headers,
    }
  );
  const dataAccountRepUser = await requestAccountRepUser.clone().json();

  return {
    ...dataBreeder.breeder,
    accountRepUser: dataAccountRepUser.user,
  };
};

export const getPuppy = async (puppyId) => {
  const headers = generateApiRequestHeaders();
  const request = await fetch(
    `${apiUrl}/api/puppy/${puppyId}?application=${environmentVariables.REACT_APP_APPLICATION_ID}&withContractTerms=1`,
    {
      method: 'GET',
      headers,
    }
  );
  const content = await request.clone().json();
  return content.puppy;
};

export const getParent = async (parentId) => {
  const headers = generateApiRequestHeaders();
  const request = await fetch(
    `${apiUrl}/api/puppy/parent/${parentId}?application=${environmentVariables.REACT_APP_APPLICATION_ID}&withActivities=1`,
    {
      method: 'GET',
      headers,
    }
  );
  const content = await request.clone().json();
  return content.puppyParent;
};

export const getLitter = async (litterId) => {
  const headers = generateApiRequestHeaders();
  const request = await fetch(
    `${apiUrl}/api/litter/${litterId}?application=${environmentVariables.REACT_APP_APPLICATION_ID}&withFood=1&withPuppies=1&withMedia=1`,
    {
      method: 'GET',
      headers,
    }
  );
  const content = await request.clone().json();
  return content.litter;
};

export const getUserById = async (userId) => {
  const { id: currentUserId } = userSession();
  if (userId && userId !== currentUserId) {
    const response = await apiGetRequest(`access/user/${userId}`);
    const user = response?.json?.user;
    return { user };
  }

  return null;
};

export const fetchAWSPermission = async (params) => {
  const {
    file: { name, size, type },
    mediaEntityId,
    mediaTypeId,
    mediaEntity,
    visibilityId = mediaVisibilityId.private,
    viewOrder = 1,
  } = params;

  const parsedName = changeFileExtToJpg(name.replace(/[^0-9a-zA-Z.]/g, ''));

  const response = await apiGetRequest(
    'media/sign',
    `name=${parsedName}&file-size=${size}&mediaTypeId=${mediaTypeId}&mediaStatusId=${1}&visibilityId=${visibilityId}&type=${type}&mediaEntity=${mediaEntity}&mediaEntityId=${mediaEntityId}&viewOrder=${viewOrder}&isDefault=${1}`
  );

  return response?.json;
};

// The API derives the S3 key extension from this name, so HEIC has to be
// normalized here too as a safety net for files that skip the client-side
// conversion in convertHeicToJpeg().
const changeFileExtToJpg = (fileName) => {
  return fileName.replace(/\.(jpeg|hei[cf])$/i, '.jpg');
};

export const sendDataToS3 = async (url, body) => {
  await fetch(url, {
    method: 'POST',
    body,
  });
};

export const uploadComplete = async (body) => {
  return await apiPostRequest('media/upload-complete', body);
};

const fetchEncodedImageById = async (mediaId) => {
  const {
    json: { encoded },
  } = await apiGetRequest(`media/${mediaId}/encode`);

  return encoded;
};

export const uploadEditCompletedImage = async (b64, imageId, format) => {
  const response = await apiPostRequest(`media/${imageId}/edit-complete`, {
    versions: [
      {
        format,
        data: b64,
      },
    ],
  });

  return response;
};

export const removeRepeteadImages = (media) => {
  const images = media.filter((item) => isMediaPhoto(item));
  const videos = media.filter((item) => isMediaVideo(item));

  for (let i = 0; i < images.length; i++) {
    const repeatedItems = [];

    if (isMediaHidden(images[i])) {
      for (let j = 0; j < images.length; j++) {
        if (
          images[i].id !== images[j].id &&
          images[i].viewOrder === images[j].viewOrder &&
          images[i].visibilityId === images[j].visibilityId
        ) {
          repeatedItems.push(images[j]);
        }
      }

      let orderedImages = [];
      if (!isEmpty(repeatedItems)) {
        orderedImages = getOrderedImages([...repeatedItems, images[i]]);
      }

      for (let k = 0; k < orderedImages.length; k++) {
        for (let l = 0; l < images.length; l++) {
          if (
            orderedImages[k].id === images[l].id &&
            orderedImages[k].checked
          ) {
            images[l].visibilityId = mediaVisibilityId.hidden;
          }
        }
      }

      orderedImages = null;
    }
  }

  return [...videos, ...images];
};

export const setImagesHidden = (media) => {
  const newMedia = removeRepeteadImages(media);

  return newMedia.map((media) => {
    if (
      (isMediaPhoto(media) &&
        media.mediaVisibilityDeferral?.visibilityId ===
          mediaVisibilityId.hidden) ||
      isEmpty(media.uploadedAt)
    ) {
      return {
        ...media,
        visibilityId: mediaVisibilityId.hidden,
      };
    }

    return media;
  });
};

export const setCheckedDefault = (media) => {
  const MAX_CHECKED_IMAGES = 8;
  const cover = getCoverImages(media);
  const additional = getAdditionalImages(media);
  const hidden = getHiddenImages(media);
  const otherMedia = getOtherMedia(media);
  const restOfImages = getRestOfImages(media);
  const deletedMedia = getDeletedMedia(media);

  const imagesToCheck = [
    ...getSortedImagesByViewOrder(cover),
    ...getSortedImagesByViewOrder(additional),
  ];

  let counter = 0;
  const imagesChecked = imagesToCheck.map((mediaItem) => {
    if (isMediaPhoto(mediaItem) && counter < MAX_CHECKED_IMAGES) {
      if (mediaItem.pendingEdit || isMediaPendingReview(mediaItem)) {
        counter = counter + 1;
        return {
          ...mediaItem,
          pendingEdit: mediaItem.pendingEdit
            ? { ...mediaItem.pendingEdit, checked: true }
            : mediaItem.pendingEdit,
          checked: true,
        };
      }
    }

    return mediaItem;
  });

  return [
    ...imagesChecked,
    ...hidden,
    ...restOfImages,
    ...otherMedia,
    ...deletedMedia,
  ];
};

export const getEncodedMedia = (media) => {
  const mediaEncoded = Promise.all(
    media.map(async (item) => {
      if (isMediaPhoto(item)) {
        return await getEncodedMediaItem(item);
      }

      return item;
    })
  );

  return mediaEncoded;
};

export const getEncodedMediaItem = async (mediaItem) => {
  const newMediaItem = cloneDeep(mediaItem);

  if (isMediaPhoto(newMediaItem)) {
    newMediaItem.encoded =
      newMediaItem?.mediaVariants?.length === 0
        ? await fetchEncodedImageById(newMediaItem?.id)
        : null;
  }

  if (newMediaItem?.pendingEdit) {
    newMediaItem.pendingEdit.encoded =
      newMediaItem?.pendingEdit?.mediaVariants?.length === 0
        ? await fetchEncodedImageById(newMediaItem?.pendingEdit?.id)
        : null;
  }

  return newMediaItem;
};

export const getMediaById = async (mediaId) => {
  const { json } = await apiGetRequest(`media/${mediaId}`);

  json.media = await getEncodedMediaItem(json.media);

  return json;
};

export const getMediaByIdAfterEdit = async (mediaId) => {
  const { json } = await apiGetRequest(`media/${mediaId}`);

  return json;
};

export const reassingParents = (body) => {
  apiPostRequest('puppy/parent/reassign', body);
};

export const getParentListings = async (parentId) => {
  const { json } = await apiGetRequest(
    'listing',
    `parentId=${parentId}&pageSize=100`
  );
  return json;
};

export const isListingValidToReassign = (listing) => {
  const listingStatusIsOk =
    isListingStatusCreated(listing) || isListingStatusLive(listing);
  const workflowStatusIsOk =
    isListingPetWorkflowStatusPendingReview(listing) ||
    isListingPetWorkflowStatusForwarded(listing) ||
    isListingPetWorkflowStatusInProgress(listing);

  return listingStatusIsOk && workflowStatusIsOk;
};

export const reassingListing = async (body) => {
  return await apiPostRequest('listing/reassign', body);
};

// *** LISTINGS LIST
// **********************************************************
export const generateCloudSearchParams = (parameters) => {
  const { sort, startPage, size, query } = parameters;
  const params = {
    options: {
      version: '2013-01-01',
      endpoint: `${environmentVariables.REACT_APP_CLOUDSEARCH_DOMAIN}`,
      region: 'us-west-2',
    },
    params: {
      queryParser: 'structured',
      query,
      start: startPage,
      sort,
      size,
    },
  };

  return params;
};

export const generateCloudSearchParamsHeaderSearch = (query) => {
  const params = {
    options: {
      version: '2013-01-01',
      endpoint: `${environmentVariables.REACT_APP_CLOUDSEARCH_DOMAIN}`,
      region: 'us-west-2',
    },
    params: {
      queryParser: 'structured',
      query,
      start: 0,
      sort: 'litter_id asc',
      size: 1000,
    },
  };

  return params;
};

export const parseListsDataFromCloudSearch = (hit) => {
  return hit.map((hitItem) => {
    const { fields } = hitItem;
    return {
      key: fields.listingId ? fields.listingId[0] : '',
      litterId: fields.litterId ? fields.litterId[0] : '',
      workflowStatus: fields.workflowStatus ? fields.workflowStatus[0] : '',
      workflowStatusId: fields.workflowStatusId
        ? fields.workflowStatusId[0]
        : '',
      puppyId: fields.puppyId ? fields.puppyId[0] : '',
      breederPuppyName: fields.breederPuppyName
        ? fields.breederPuppyName[0]
        : '',
      breedAdLabel: fields.breedAdLabel ? fields.breedAdLabel[0] : '',
      breeder: fields.breeder ? fields.breeder[0] : '',
      createdAt: fields.createdAt ? date.toUSLocale(fields.createdAt[0]) : '',
      logicalUpdatedAt: fields.logicalUpdatedAt
        ? date.toUSLocale(fields.logicalUpdatedAt[0])
        : '',
      listingStatus: fields.listingStatus ? fields.listingStatus[0] : '',
      listingStatusId: fields.listingStatusId ? fields.listingStatusId[0] : '',
      taskOverView: 'not in API',
    };
  });
};

export const postponePuppyParent = async (
  params,
  dispatch,
  postponeListingErrorObject
) => {
  const headers = generateApiRequestHeaders();
  const parents = [];
  const parentsParams = [];

  if (
    includes(
      params.appliesTo,
      PET_CONFIG.listingView.pendingBreederAppliestTo.sire
    )
  ) {
    const innerParams = {
      otherReason: params.otherReason.find(
        (or) =>
          or.entity === PET_CONFIG.listingView.pendingBreederAppliestTo.sire
      )?.value,
      reasons: params.reasons
        .filter(
          (or) =>
            or.entity === PET_CONFIG.listingView.pendingBreederAppliestTo.sire
        )
        .map((or) => or.value),
    };

    parents.push(params.parentIds.sire);
    parentsParams.push(innerParams);
  }

  if (
    includes(
      params.appliesTo,
      PET_CONFIG.listingView.pendingBreederAppliestTo.dam
    )
  ) {
    const innerParams = {
      otherReason: params.otherReason.find(
        (or) =>
          or.entity === PET_CONFIG.listingView.pendingBreederAppliestTo.dam
      )?.value,
      reasons: params.reasons
        .filter(
          (or) =>
            or.entity === PET_CONFIG.listingView.pendingBreederAppliestTo.dam
        )
        .map((or) => or.value),
    };
    parents.push(params.parentIds.dam);
    parentsParams.push(innerParams);
  }

  const resp = await posponeByParent(
    zipObject(parents, parentsParams),
    headers,
    dispatch,
    postponeListingErrorObject
  );

  return resp;
};

const posponeByParent = async (query, headers, dispatch, errorObject) => {
  try {
    const paramsBody = {
      puppyParents: {
        ...query,
      },
    };
    const request = await fetch(
      `${apiUrl}/api/puppy/parent/postpone?application=${environmentVariables.REACT_APP_APPLICATION_ID}`,
      {
        method: 'POST',
        headers,
        body: JSON.stringify(paramsBody),
      }
    );

    const resp = await request.clone().json();
    return resp;
  } catch (error) {
    dispatch(errorObject(error));
  }
};

export const postponeListings = async (
  headers,
  paramsBody,
  dispatch,
  errorObject
) => {
  const request = await fetch(
    `${apiUrl}/api/listing/postpone?application=${environmentVariables.REACT_APP_APPLICATION_ID}`,
    {
      method: 'POST',
      headers,
      body: JSON.stringify(paramsBody),
    }
  );

  const resp = await request.clone().json();

  if (resp?.statusCode === 405) {
    dispatch(errorObject(`Postpone listing ${resp.message}`));
  }
  if (Object.keys(resp.listings.error).length > 0) {
    dispatch(
      errorObject(`Postpone listing ${JSON.stringify(resp.listings.error)}`)
    );
  }

  return resp;
};
